import { Text, View, type StyleProp, type ViewStyle } from "react-native";
import {
  CircleAlert,
  CircleCheck,
  Info,
  TriangleAlert,
  X,
  type LucideIcon,
} from "lucide-react-native";
import { colors, semanticColors, typography } from "../theme/tokens";
import { CompactActionButton } from "./CompactActionButton";
import { IconButton } from "./IconButton";

type Variant = "info" | "warning" | "encouragement" | "important";

type Props = {
  variant: Variant;
  /** The notice text. May be left out when the title says it all. */
  message?: string;
  /** Optional bold first line. With a title, the action moves under the text. */
  title?: string;
  /** Replaces the tone's default icon when a more specific one says more. */
  icon?: LucideIcon;
  actionLabel?: string;
  onAction?: () => void;
  /** Stable analytics identifier; `actionLabel` travels as the event label. */
  action?: string;
  /** Icon on the action. Retries pass RotateCcw, like every "Try again". */
  actionIcon?: LucideIcon;
  /** Shows the action's spinner while it runs. */
  actionLoading?: boolean;
  /** Shows a close ✕. The caller decides whether the dismissal is remembered. */
  onDismiss?: () => void;
  dismissLabel?: string;
  /** Stable analytics identifier for the close ✕. */
  dismissAction?: string;
  /** On a dark card or texture: the on-dark fill and white word levels. */
  onDark?: boolean;
  testID?: string;
  style?: StyleProp<ViewStyle>;
};

const ICON: Record<Variant, LucideIcon> = {
  info: Info,
  warning: TriangleAlert,
  encouragement: CircleCheck,
  important: CircleAlert,
};

const TEXT_COLOR = colors.textMid;

/**
 * Every notice sits on the same quiet neutral wash, with no border, so it
 * blends into the surface. An error is marked by a red icon and a warning by
 * a yellow triangle; everything else keeps the icon in the text colour.
 */
const ICON_COLOR: Record<Variant, string> = {
  important: semanticColors.status.danger,
  warning: semanticColors.status.warning,
  info: TEXT_COLOR,
  encouragement: TEXT_COLOR,
};

const ON_DARK_ICON_COLOR: Record<Variant, string> = {
  important: semanticColors.onDark.danger,
  warning: semanticColors.status.warning,
  info: semanticColors.onDark.text.secondary,
  encouragement: semanticColors.onDark.text.secondary,
};

/**
 * A short notice inside a screen, card or dialog: something failed, needs
 * attention, or is worth knowing. A message about a single input belongs under
 * that input as a FieldMessage instead.
 */
export function ActionBanner({
  variant,
  message,
  title,
  icon,
  actionLabel,
  onAction,
  action,
  actionIcon,
  actionLoading,
  onDismiss,
  dismissLabel,
  dismissAction,
  onDark = false,
  testID,
  style,
}: Props) {
  const Icon = icon ?? ICON[variant];
  const actionButton =
    actionLabel && onAction ? (
      <CompactActionButton
        label={actionLabel}
        action={action}
        onPress={onAction}
        variant="primary"
        size="sm"
        leadingIcon={actionIcon}
        loading={actionLoading}
      />
    ) : null;
  const needsAttention = variant === "important" || variant === "warning";

  return (
    <View
      testID={testID}
      accessibilityRole={needsAttention ? "alert" : undefined}
      accessibilityLiveRegion={needsAttention ? "polite" : undefined}
      style={[
        {
          backgroundColor: onDark
            ? semanticColors.onDark.fill.subtle
            : semanticColors.notice.quiet.background,
          borderRadius: 12,
          paddingHorizontal: 11,
          paddingVertical: 9,
          gap: 8,
        },
        style,
      ]}
    >
      <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
        <Icon
          size={14}
          color={(onDark ? ON_DARK_ICON_COLOR : ICON_COLOR)[variant]}
          strokeWidth={2}
          style={{ alignSelf: "flex-start", marginTop: 1 }}
        />
        <View style={{ flex: 1, gap: 2 }}>
          {title ? (
            <Text
              style={{
                fontFamily: typography.families.sansBold,
                fontSize: 12,
                lineHeight: 17,
                fontWeight: "700",
                color: onDark
                  ? semanticColors.onDark.text.primary
                  : semanticColors.text.primary,
              }}
            >
              {title}
            </Text>
          ) : null}
          {message ? (
            <Text
              style={{
                fontFamily: typography.families.sans,
                fontSize: 12,
                lineHeight: 17,
                color: onDark
                  ? semanticColors.onDark.text.secondary
                  : TEXT_COLOR,
              }}
            >
              {message}
            </Text>
          ) : null}
        </View>
        {title ? null : actionButton}
        {onDismiss ? (
          <IconButton
            onPress={onDismiss}
            accessibilityLabel={dismissLabel ?? "Dismiss"}
            action={dismissAction}
            hitSlop={8}
            size={22}
            pressedOpacity={0.58}
            style={{ alignSelf: "flex-start", marginTop: -3 }}
          >
            <X
              size={14}
              color={
                onDark ? semanticColors.onDark.text.tertiary : colors.textLight
              }
              strokeWidth={1.8}
            />
          </IconButton>
        ) : null}
      </View>
      {title && actionButton ? (
        <View style={{ paddingLeft: 22, alignItems: "flex-start" }}>
          {actionButton}
        </View>
      ) : null}
    </View>
  );
}

export default ActionBanner;
